import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { MapPin, Calendar, Trophy, ArrowLeft, Heart, HeartStraight, WhatsappLogo, TwitterLogo, LinkSimple, Check, CurrencyInr } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import CategoryBadges from '../components/CategoryBadges';
import PaymentProofUpload from '../components/PaymentProofUpload';
import { AGE_CATEGORIES } from '../lib/tournamentCategories';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ic = "w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-2.5 text-sm outline-none transition-all";
const lc = "block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider";

const emptyRoster = (n) => Array.from({ length: n }, () => '');

const TournamentDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [regError, setRegError] = useState('');
  const [reg, setReg] = useState({
    player_name: '', team_name: '', captain_name: '', captain_email: '',
    phone: '', email: '', city: '', age_category: '', gender_category: '',
    players: [], substitutes: ['', '', ''], payment_screenshot_url: ''
  });

  const fetchTournament = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/${id}`);
      setTournament(data);
    } catch (e) {
      logError('Failed to load tournament:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const checkSaved = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/saved-tournaments/check/${id}`, { withCredentials: true });
      setSaved(data.saved);
    } catch (e) {
      logError('Failed to check saved status:', e);
    }
  }, [id]);

  useEffect(() => {
    fetchTournament();
    if (user) checkSaved();
  }, [id, user, fetchTournament, checkSaved]);

  const teamSize = useMemo(() => {
    if (!tournament) return 0;
    if (tournament.format === '3v3') return 3;
    if (tournament.format === '5v5') return 5;
    return 0;
  }, [tournament]);

  const isPaid = tournament && (
    tournament.payment_method === 'upi' &&
    (
      (tournament.fee_mode === 'per_category' && tournament.category_fees && Object.values(tournament.category_fees).some((v) => Number(v) > 0)) ||
      (tournament.fee_mode !== 'per_category' && (tournament.registration_fee || 0) > 0)
    )
  );

  const isPerCategory = tournament && tournament.fee_mode === 'per_category';

  const computed = tournament?.computed_status;
  const registrationClosed = computed === 'registration_closed';
  const tournamentEnded = computed === 'ended';
  const canRegister = tournament?.status === 'approved' && !registrationClosed && !tournamentEnded;

  const [modalFee, setModalFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  // Fetch effective fee from server whenever age/gender selection changes in the modal.
  useEffect(() => {
    if (!showModal || !tournament) return;
    if (!reg.age_category) { setModalFee(null); return; }
    let cancelled = false;
    setFeeLoading(true);
    axios.post(`${API}/tournaments/${tournament.tournament_id}/fee-quote`, {
      age_category: reg.age_category,
      gender_category: reg.gender_category || (tournament.gender_categories && tournament.gender_categories[0]) || ''
    })
      .then(({ data }) => { if (!cancelled) setModalFee(data.fee); })
      .catch(() => { if (!cancelled) setModalFee(null); })
      .finally(() => { if (!cancelled) setFeeLoading(false); });
    return () => { cancelled = true; };
  }, [showModal, reg.age_category, reg.gender_category, tournament]);

  const openRegisterModal = () => {
    if (!user) { navigate('/login'); return; }
    // Prefill from profile
    setReg({
      player_name: user.name || '',
      team_name: '',
      captain_name: user.name || '',
      captain_email: user.email || '',
      phone: user.phone || '',
      email: user.email || '',
      city: user.city || '',
      age_category: (tournament.age_categories && tournament.age_categories[0]) || '',
      gender_category: (tournament.gender_categories && tournament.gender_categories[0]) || '',
      players: emptyRoster(teamSize),
      substitutes: emptyRoster(tournament.format === '5v5' ? 3 : 2),
      payment_screenshot_url: ''
    });
    // Auto-set captain in slot 1
    setTimeout(() => {
      setReg((prev) => {
        const players = [...prev.players];
        players[0] = user.name || '';
        return { ...prev, players };
      });
    }, 0);
    setRegError('');
    setShowModal(true);
  };

  const toggleSave = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (saved) { await axios.delete(`${API}/saved-tournaments/${id}`, { withCredentials: true }); setSaved(false); }
      else { await axios.post(`${API}/saved-tournaments/${id}`, {}, { withCredentials: true }); setSaved(true); }
    } catch (e) {
      logError('Failed to toggle save:', e);
    }
  };

  const setPlayerAt = (idx, val) => {
    const players = [...reg.players];
    players[idx] = val;
    setReg({ ...reg, players });
  };

  const setSubAt = (idx, val) => {
    const substitutes = [...reg.substitutes];
    substitutes[idx] = val;
    setReg({ ...reg, substitutes });
  };

  const submitRegistration = async (e) => {
    e.preventDefault();
    if (isPaid && (modalFee || 0) > 0 && !reg.payment_screenshot_url) {
      setRegError('Please upload your payment screenshot before submitting.');
      return;
    }
    setSubmitting(true);
    setRegError('');
    const registration_type = tournament.format === '1v1' ? 'player' : 'team';
    const payload = {
      tournament_id: tournament.tournament_id,
      registration_type,
      phone: reg.phone,
      city: reg.city,
      age_category: reg.age_category,
      gender_category: reg.gender_category,
      payment_screenshot_url: reg.payment_screenshot_url || null
    };
    if (registration_type === 'player') {
      payload.player_name = reg.player_name;
      payload.email = reg.email;
    } else {
      payload.team_name = reg.team_name;
      payload.captain_name = reg.captain_name;
      payload.captain_email = reg.captain_email;
      payload.num_players = teamSize;
      payload.players = reg.players.filter((p) => p && p.trim());
      payload.substitutes = reg.substitutes.filter((p) => p && p.trim());
    }

    try {
      await axios.post(`${API}/registrations`, payload, { withCredentials: true });
      setShowModal(false);
      navigate('/player-dashboard');
    } catch (err) {
      setRegError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  if (!tournament) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="text-gray-500">Tournament not found</div></div>;

  // Fee display for the sidebar — supports per_category "starts from"
  const feeDisplay = (() => {
    if (isPerCategory && tournament.category_fees) {
      const vals = Object.values(tournament.category_fees).map(Number).filter((v) => !isNaN(v));
      if (vals.length === 0) return 'FREE';
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      if (min === 0 && max === 0) return 'FREE';
      return min === max ? `₹${min}` : `From ₹${min}`;
    }
    return (tournament.registration_fee || 0) > 0 ? `₹${tournament.registration_fee}` : 'FREE';
  })();

  return (
    <>
      <SEO
        title={`${tournament.name} | Basketball Tournament in ${tournament.city}`}
        description={`${tournament.name} — ${tournament.format} basketball tournament in ${tournament.city}, ${tournament.state} on ${tournament.dates}. ${tournament.description?.slice(0, 100) || 'Register on Beyond Ball India.'}`}
        path={`/tournaments/${id}`}
        image={tournament.banner_url}
      />

      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tournaments" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6 transition-colors" data-testid="back-to-tournaments-link">
            <ArrowLeft size={16} weight="bold" /> Back to tournaments
          </Link>

          {/* Banner — details page shows the FULL original poster (no crop). */}
          <div className="relative rounded-3xl overflow-hidden bg-[#0F1A2E] mb-8 animate-fade-up" data-testid="tournament-banner-full">
            {tournament.banner_url ? (
              <img
                src={tournament.banner_url}
                alt={tournament.name}
                className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              />
            ) : (
              <div className="aspect-[21/9] flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-50">
                <span className="text-9xl font-bold text-orange-200">{tournament.format}</span>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 animate-fade-up delay-100">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold uppercase border border-orange-100">{tournament.format}</span>
                <CategoryBadges
                  ages={tournament.age_categories || []}
                  genders={tournament.gender_categories || []}
                  size="md"
                />
              </div>

              <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-4" data-testid="tournament-name">{tournament.name}</h1>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-600 mb-8">
                <div className="flex items-center gap-1.5"><MapPin size={16} weight="bold" /> {tournament.city}, {tournament.state}</div>
                <div className="flex items-center gap-1.5"><Calendar size={16} weight="bold" /> {tournament.dates}</div>
              </div>

              {tournament.description && (
                <div className="mb-8">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">About</h3>
                  <p className="text-gray-700 leading-relaxed">{tournament.description}</p>
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Venue</h3>
                <p className="text-gray-900">{tournament.venue}</p>
              </div>

              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Rules</h3>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{tournament.rules}</p>
              </div>

              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Trophy size={14} weight="bold" /> Prizes</h3>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{tournament.prizes}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="animate-fade-up delay-200">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-24">
                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Registration Deadline</div>
                    <div className="text-base font-bold text-[#F97316]">{tournament.registration_deadline}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Registration Fee</div>
                    <div className="text-2xl font-bold text-gray-900 flex items-center gap-0.5" data-testid="registration-fee-display">
                      {feeDisplay}
                    </div>
                  </div>
                </div>

                {(registrationClosed || tournamentEnded) && (
                  <div className={`mb-3 text-center px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border ${tournamentEnded ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="tournament-status-badge">
                    {tournamentEnded ? 'Tournament Ended' : 'Registration Closed'}
                  </div>
                )}

                <button
                  onClick={openRegisterModal}
                  disabled={!canRegister}
                  className={`btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold mb-2 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-200`}
                  data-testid="register-tournament-btn"
                >
                  {tournamentEnded
                    ? 'Tournament Ended'
                    : registrationClosed
                      ? 'Registration Closed'
                      : (tournament.format === '1v1' ? 'Register as Player' : 'Register as Team')}
                </button>

                <button onClick={toggleSave} className={`w-full px-5 py-3 rounded-xl text-sm font-semibold border transition-colors flex items-center justify-center gap-2 ${saved ? 'bg-orange-50 border-orange-200 text-[#F97316]' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`} data-testid="save-tournament-btn">
                  {saved ? <Heart size={16} weight="fill" /> : <HeartStraight size={16} weight="bold" />}
                  {saved ? 'Saved' : 'Save Tournament'}
                </button>

                <div className="pt-4 mt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-2 text-center">Share</div>
                  <div className="grid grid-cols-3 gap-2">
                    <a href={`https://wa.me/?text=${encodeURIComponent(`Check out ${tournament.name} on Beyond Ball India: ${window.location.href}`)}`} target="_blank" rel="noopener noreferrer" className="bg-gray-50 hover:bg-[#25D366] hover:text-white text-gray-700 p-2.5 rounded-lg flex items-center justify-center transition-colors" data-testid="share-whatsapp-btn" aria-label="Share on WhatsApp">
                      <WhatsappLogo size={18} weight="bold" />
                    </a>
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${tournament.name} on Beyond Ball India`)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="bg-gray-50 hover:bg-[#1DA1F2] hover:text-white text-gray-700 p-2.5 rounded-lg flex items-center justify-center transition-colors" data-testid="share-twitter-btn" aria-label="Share on Twitter">
                      <TwitterLogo size={18} weight="bold" />
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="bg-gray-50 hover:bg-[#F97316] hover:text-white text-gray-700 p-2.5 rounded-lg flex items-center justify-center transition-colors" data-testid="share-copy-btn" aria-label="Copy link">
                      {copied ? <Check size={18} weight="bold" /> : <LinkSimple size={18} weight="bold" />}
                    </button>
                  </div>
                </div>

                <div className="text-center text-xs text-gray-500 mt-4">
                  Organized by{' '}
                  <Link to={`/organizer-profile/${tournament.organizer_id}`} className="text-[#F97316] hover:text-orange-700 font-semibold" data-testid="organizer-profile-link">
                    {tournament.organizer_name}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Registration modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto animate-fade-in">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl my-6" data-testid="registration-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {tournament.format === '1v1' ? 'Register as Player' : 'Register as Team'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{tournament.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {regError && (
                <div className="bg-red-50 border border-red-100 text-red-700 px-3 py-2 mb-3 text-xs rounded-lg" data-testid="registration-error">
                  {regError}
                </div>
              )}

              <form onSubmit={submitRegistration} className="space-y-4">
                {tournament.format === '1v1' ? (
                  <>
                    <div>
                      <label className={lc}>Full Name *</label>
                      <input required type="text" value={reg.player_name} onChange={(e) => setReg({ ...reg, player_name: e.target.value })} className={ic} data-testid="reg-player-name" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={lc}>Phone Number *</label>
                        <input required type="tel" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} className={ic} data-testid="reg-phone" />
                      </div>
                      <div>
                        <label className={lc}>Email *</label>
                        <input required type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className={ic} data-testid="reg-email" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className={lc}>Team Name *</label>
                      <input required type="text" value={reg.team_name} onChange={(e) => setReg({ ...reg, team_name: e.target.value })} className={ic} data-testid="reg-team-name" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={lc}>Captain Name *</label>
                        <input required type="text" value={reg.captain_name} onChange={(e) => setReg({ ...reg, captain_name: e.target.value })} className={ic} data-testid="reg-captain-name" />
                      </div>
                      <div>
                        <label className={lc}>Captain Phone *</label>
                        <input required type="tel" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} className={ic} data-testid="reg-captain-phone" />
                      </div>
                    </div>
                    <div>
                      <label className={lc}>Captain Email *</label>
                      <input required type="email" value={reg.captain_email} onChange={(e) => setReg({ ...reg, captain_email: e.target.value })} className={ic} data-testid="reg-captain-email" />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={lc}>City *</label>
                    <input required type="text" value={reg.city} onChange={(e) => setReg({ ...reg, city: e.target.value })} className={ic} data-testid="reg-city" />
                  </div>
                  <div>
                    <label className={lc}>Age Category *</label>
                    <select required value={reg.age_category} onChange={(e) => setReg({ ...reg, age_category: e.target.value })} className={ic} data-testid="reg-age-category">
                      <option value="">Select…</option>
                      {(tournament.age_categories && tournament.age_categories.length > 0 ? tournament.age_categories : AGE_CATEGORIES).map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={lc}>Gender Category *</label>
                    <select required value={reg.gender_category} onChange={(e) => setReg({ ...reg, gender_category: e.target.value })} className={ic} data-testid="reg-gender-category">
                      <option value="">Select…</option>
                      {(tournament.gender_categories || []).map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {reg.age_category && reg.gender_category && modalFee !== null && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="modal-fee-display">
                    <span className="text-sm text-gray-700">Registration Fee</span>
                    <span className="text-lg font-bold text-gray-900">
                      {feeLoading ? '…' : (modalFee > 0 ? `₹${modalFee}` : 'FREE')}
                    </span>
                  </div>
                )}

                {tournament.format !== '1v1' && (
                  <div>
                    <label className={lc}>Team Players ({teamSize} required)</label>
                    <div className="space-y-2">
                      {reg.players.map((p, i) => (
                        <input
                          key={`roster-slot-${i}`}
                          required
                          type="text"
                          placeholder={`Player ${i + 1}${i === 0 ? ' (Captain)' : ''} name`}
                          value={p}
                          onChange={(e) => setPlayerAt(i, e.target.value)}
                          className={ic}
                          data-testid={`reg-player-${i}`}
                        />
                      ))}
                    </div>

                    <label className={`${lc} mt-4`}>Substitutes (optional)</label>
                    <div className="space-y-2">
                      {reg.substitutes.map((p, i) => (
                        <input
                          key={`sub-slot-${i}`}
                          type="text"
                          placeholder={`Substitute ${i + 1} name (optional)`}
                          value={p}
                          onChange={(e) => setSubAt(i, e.target.value)}
                          className={ic}
                          data-testid={`reg-substitute-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {isPaid && modalFee > 0 && (
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 space-y-3" data-testid="payment-info-block">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-orange-700 mb-1">Pay the Organizer</p>
                      <p className="text-sm text-gray-700">
                        Send <strong>₹{modalFee}</strong> via UPI, then upload the screenshot below to complete your registration.
                      </p>
                    </div>

                    {tournament.upi_qr_url && (
                      <div className="flex justify-center">
                        <img
                          src={tournament.upi_qr_url}
                          alt="UPI QR code"
                          className="w-40 h-40 object-contain bg-white rounded-xl border border-orange-200"
                          data-testid="upi-qr-image"
                        />
                      </div>
                    )}

                    <div>
                      <p className="text-xs text-gray-500 uppercase font-semibold">UPI ID</p>
                      <p className="text-sm font-bold text-gray-900 select-all break-all" data-testid="upi-id-display">{tournament.upi_id}</p>
                    </div>

                    {tournament.payment_instructions && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold">Instructions</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{tournament.payment_instructions}</p>
                      </div>
                    )}

                    <div className="pt-2 border-t border-orange-100">
                      <label className={lc}>Payment Screenshot *</label>
                      <PaymentProofUpload
                        value={reg.payment_screenshot_url}
                        onChange={(url) => setReg({ ...reg, payment_screenshot_url: url })}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-semibold">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                    data-testid="submit-registration-btn"
                  >
                    {submitting ? 'Submitting…' : 'Submit Registration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TournamentDetails;
